import React, { useCallback, useEffect, useRef, useState } from 'react';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import { Fingerprint } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import TextField from '@/components/ui/text-field';
import { cn } from '@/lib/utils';
import {
  AdminStepUpCancelledError,
  authenticateWithAdminPasskey,
  describeAdminStepUpError,
  fetchAdminPasskeys,
  getAdminStepUpErrorCode,
  registerAdminPasskey,
} from '../../../../services/adminStepUp';
import type { AdminStepUpPromptReason } from '../../../../slices/adminStepUp/adminStepUpSlice';
import { SESSION_NOT_FRESH_CODE } from '../../../../services/sessionFreshness';
import FreshSignInPrompt from '../../Account/AccountSecurity/FreshSignInPrompt';
import { isAxiosStatus } from '../../../../services/apiError';

export interface AdminStepUpPanelProps {
  /** Called with the new step-up token once the admin has verified. */
  onVerified: (token: string) => void;
  /** Renders a secondary Cancel action when given (the modal). */
  onCancel?: () => void;
  /** Why verification is being asked for; tunes the intro line. */
  reason?: AdminStepUpPromptReason | null;
  /** Styles the controls for the glass modal surface. */
  context?: 'default' | 'modal';
  /**
   * The full-page gate passes this: called instead of showing the panel when
   * the backend does not enforce step-up (`stepUp.enforced` is false, or the
   * passkey routes 404 on a backend without the feature).
   */
  onStepUpNotRequired?: () => void;
}

type LoadState =
  | { status: 'loading' }
  | { status: 'failed' }
  | { status: 'ready'; hasPasskeys: boolean };

type Feedback = { tone: 'info' | 'error'; text: string } | null;

const DEFAULT_PASSKEY_LABEL = 'MacBook';

const INTRO_BY_REASON: Record<AdminStepUpPromptReason, string> = {
  expired:
    'Your admin verification on this device has expired. Verify again to continue.',
  required: 'This admin action needs a passkey verification.',
};

/**
 * The admin passkey step-up body (ORA-864), shared by the full-page gate in
 * AdminRoute and the step-up modal: enrolls a first passkey or verifies with
 * an existing one, then hands the step-up token to `onVerified`.
 */
const AdminStepUpPanel: React.FC<AdminStepUpPanelProps> = ({
  onVerified,
  onCancel,
  reason = null,
  context = 'default',
  onStepUpNotRequired,
}) => {
  const [isSupported] = useState(() => browserSupportsWebAuthn());
  // Kept in a ref so an inline callback does not re-run the load.
  const onStepUpNotRequiredRef = useRef(onStepUpNotRequired);
  onStepUpNotRequiredRef.current = onStepUpNotRequired;

  // The gate must learn whether step-up is enforced even on a browser without
  // passkeys, so it can let the admin through when it is not.
  const shouldLoad = isSupported || Boolean(onStepUpNotRequired);

  const [loadState, setLoadState] = useState<LoadState>({
    status: 'loading',
  });

  const [label, setLabel] = useState(DEFAULT_PASSKEY_LABEL);
  const [isWorking, setIsWorking] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>(null);
  // The first passkey may only be set up within minutes of signing in
  // (ORA-1145); past that the backend answers SESSION_NOT_FRESH and the only
  // way on is to sign out and back in.
  const [needsFreshSignIn, setNeedsFreshSignIn] = useState(false);

  const load = useCallback(async () => {
    setLoadState({ status: 'loading' });
    try {
      const { passkeys, stepUp } = await fetchAdminPasskeys();
      const notRequired = onStepUpNotRequiredRef.current;

      if (notRequired && !stepUp.enforced) {
        notRequired();
        return;
      }

      setLoadState({ status: 'ready', hasPasskeys: passkeys.length > 0 });
    } catch (error) {
      const notRequired = onStepUpNotRequiredRef.current;

      if (notRequired && isAxiosStatus(error, 404)) {
        notRequired();
        return;
      }

      setLoadState({ status: 'failed' });
    }
  }, []);

  useEffect(() => {
    if (shouldLoad) void load();
  }, [shouldLoad, load]);

  const runCeremony = async (ceremony: () => Promise<string>) => {
    setIsWorking(true);
    setFeedback(null);
    try {
      const token = await ceremony();
      onVerified(token);
    } catch (error) {
      const code = getAdminStepUpErrorCode(error);
      if (code === SESSION_NOT_FRESH_CODE) {
        setNeedsFreshSignIn(true);
        return;
      }
      // The list changed under us (a passkey was removed or added elsewhere):
      // show the right step instead of a dead end.
      if (code === 'NO_ADMIN_PASSKEY') {
        setLoadState({ status: 'ready', hasPasskeys: false });
      } else if (code === 'ADMIN_STEP_UP_REQUIRED') {
        setLoadState({ status: 'ready', hasPasskeys: true });
      }

      setFeedback({
        tone: error instanceof AdminStepUpCancelledError ? 'info' : 'error',
        text: describeAdminStepUpError(error),
      });
    } finally {
      setIsWorking(false);
    }
  };

  // Inside the step-up modal, a prompt for the enrolment call would wait on
  // the very modal that is making it.
  const handleRegister = () =>
    runCeremony(
      async () =>
        (await registerAdminPasskey(label, { skipPrompt: context === 'modal' }))
          .stepUp.token
    );

  const handleAuthenticate = () =>
    runCeremony(async () => (await authenticateWithAdminPasskey()).token);

  const cancelButton = onCancel ? (
    <Button
      variant="outline"
      context={context}
      onClick={onCancel}
      disabled={isWorking}
    >
      Cancel
    </Button>
  ) : null;

  const renderBody = () => {
    // Without passkey support, the gate still waits for the status call: it
    // may let the admin straight through.
    const isCheckingStatus = shouldLoad && loadState.status === 'loading';

    if (!isSupported && !isCheckingStatus) {
      return (
        <p className="text-fg-secondary">
          This browser does not support passkeys. Open the admin dashboard in a
          current version of Chrome, Safari, Edge or Firefox.
        </p>
      );
    }

    if (loadState.status === 'loading') {
      return (
        <div
          className="flex items-center gap-3 text-fg-secondary"
          role="status"
          aria-live="polite"
        >
          <Spinner size={16} />
          Checking your passkeys...
        </div>
      );
    }

    if (loadState.status === 'failed') {
      return (
        <div className="flex flex-col gap-4">
          <p className="text-status-danger" role="alert">
            Could not load your passkeys.
          </p>
          <div className="flex flex-wrap justify-end gap-3">
            {cancelButton}
            <Button context={context} onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      );
    }

    if (!loadState.hasPasskeys && needsFreshSignIn) {
      return (
        <div className="flex flex-col gap-4">
          <FreshSignInPrompt
            context={context}
            message="For security, your first passkey can only be set up within a few minutes of signing in. Sign out and back in, then set it up."
            returnTo="/admin"
          />
          {cancelButton && (
            <div className="flex flex-wrap justify-end gap-3">
              {cancelButton}
            </div>
          )}
        </div>
      );
    }

    if (!loadState.hasPasskeys) {
      return (
        <div className="flex flex-col gap-4">
          <p className="text-fg-secondary">
            The admin dashboard needs a passkey. Set one up on this device with
            Touch ID, Windows Hello or a security key. You will verify with it
            once a day.
          </p>
          <TextField
            label="Passkey name"
            value={label}
            onChange={(event) => setLabel(event.target.value)}
            placeholder={DEFAULT_PASSKEY_LABEL}
            context={context}
            disabled={isWorking}
            maxLength={60}
            fullWidth
          />
          <div className="flex flex-wrap justify-end gap-3">
            {cancelButton}
            <Button onClick={handleRegister} disabled={isWorking}>
              {isWorking ? <Spinner size={16} /> : <Fingerprint />}
              Set up a passkey
            </Button>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4">
        <p className="text-fg-secondary">
          {reason
            ? INTRO_BY_REASON[reason]
            : 'Verify with your passkey to open the admin dashboard. It stays verified on this device for 24 hours.'}
        </p>
        <div className="flex flex-wrap justify-end gap-3">
          {cancelButton}
          <Button onClick={handleAuthenticate} disabled={isWorking}>
            {isWorking ? <Spinner size={16} /> : <Fingerprint />}
            Verify with passkey
          </Button>
        </div>
      </div>
    );
  };

  const title =
    isSupported && loadState.status === 'ready' && !loadState.hasPasskeys
      ? 'Set up a passkey'
      : "Verify it's you";

  return (
    <div className="flex flex-col gap-4">
      <h2 className="m-0 text-xl text-fg-primary">{title}</h2>
      {renderBody()}
      {feedback && (
        <p
          className={cn(
            'text-sm',
            feedback.tone === 'error'
              ? 'text-status-danger'
              : 'text-fg-secondary'
          )}
          role={feedback.tone === 'error' ? 'alert' : 'status'}
        >
          {feedback.text}
        </p>
      )}
    </div>
  );
};

export default AdminStepUpPanel;
